import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { setProgressBarActive } from 'reducers/pageReducer';
import { useDispatch } from 'react-redux';
import {
  getDashboardLanguage,
  localizeDashboardError,
  translateDashboardText,
} from 'i18n/dashboardLanguage';

interface AsyncState<T> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
}

export const useAsyncOperation = <T>() => {
  const dispatch = useDispatch();
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    error: null,
    isLoading: false
  });

  const execute = async <R>(
    operation: () => Promise<R>,
    {
      showProgress = true,
      showError = true,
      onSuccess,
      onError,
      successMessage,
      errorMessage: customErrorMessage,
    }: {
      showProgress?: boolean;
      showError?: boolean;
      onSuccess?: (result: R) => void;
      onError?: (error: Error) => void;
      successMessage?: string;
      errorMessage?: string;
    } = {}
  ) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      if (showProgress) {
        dispatch(setProgressBarActive(true));
      }

      const result = await operation();

      onSuccess?.(result);
      setState(prev => ({ ...prev, data: result as any }));
      if (successMessage) {
        enqueueSnackbar(translateDashboardText(successMessage), { variant: 'success' });
      }
      return result;
    } catch (error) {
      const defaultErrorMsg =
        error instanceof Error ? error.message : 'An unknown error occurred';
      const finalErrorMessage = customErrorMessage || defaultErrorMsg;
      if (showError) {
        enqueueSnackbar(localizeDashboardError(finalErrorMessage), { variant: 'error' });
      }
      const errorObj =
        getDashboardLanguage() === 'am'
          ? new Error(localizeDashboardError(finalErrorMessage))
          : error instanceof Error
            ? error
            : new Error(finalErrorMessage);
      setState(prev => ({ ...prev, error: errorObj }));
      onError?.(errorObj);
      throw errorObj;
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
      if (showProgress) {
        dispatch(setProgressBarActive(false));
      }
    }
  };

  return {
    execute,
    ...state
  };
};
